'use client';

import * as React from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import AutoScroll from 'embla-carousel-auto-scroll';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Define the context shape
type CarouselApi = UseEmblaCarouselType[1];
type CarouselContextProps = {
  api: CarouselApi;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

// Custom hook to access the context
export function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel /> component');
  }
  return context;
}

// Main Carousel component
interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  showArrows?: boolean;
  showDots?: boolean;
  options?: Parameters<typeof useEmblaCarousel>[0];
  autoScroll?: boolean | Parameters<typeof AutoScroll>[0];
}

export function Carousel({
  children,
  className,
  showArrows = true,
  showDots = true,
  options,
  autoScroll = false,
  ...props
}: CarouselProps) {
  const plugins = React.useMemo(() => {
    return autoScroll ? [AutoScroll(typeof autoScroll === 'object' ? autoScroll : { speed: 1, stopOnInteraction: false })] : [];
  }, [autoScroll]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, ...options },
    plugins
  );

  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(options?.startIndex ?? 0);

  const scrollPrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = React.useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Prevent initial flash of first slide by snapping to startIndex ASAP
  const didInit = React.useRef(false);
  React.useLayoutEffect(() => {
    if (!emblaApi || didInit.current) return;
    const idx = options?.startIndex ?? 0;
    try {
      emblaApi.scrollTo(idx, true);
      onSelect(emblaApi);
    } finally {
      didInit.current = true;
    }
  }, [emblaApi, options?.startIndex, onSelect]);

  const contextValue = {
    api: emblaApi,
    scrollPrev,
    scrollNext,
    scrollTo,
    canScrollPrev,
    canScrollNext,
    selectedIndex,
  };

  return (
    <CarouselContext.Provider value={contextValue}>
      <div className={cn('relative', className)} {...props}>
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex items-center -ml-2 sm:-ml-4">{children}</div>
        </div>

        {showArrows && (
          <div className="hidden md:block carousel-arrows">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="carousel-arrow absolute h-8 w-8 rounded-full left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Previous</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="carousel-arrow absolute h-8 w-8 rounded-full right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10"
            >
              <ArrowRight className="h-4 w-4" />
              <span className="sr-only">Next</span>
            </Button>
          </div>
        )}

        {showDots && emblaApi && (
          <div className="carousel-dots hidden lg:flex justify-center space-x-2 mt-4">
            {emblaApi.scrollSnapList().map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(
                  'h-2 w-2 rounded-none transition-colors',
                  index === selectedIndex ? 'bg-primary' : 'bg-muted hover:bg-primary/50'
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </CarouselContext.Provider>
  );
}

// CarouselItem component
type CarouselItemProps = React.HTMLAttributes<HTMLDivElement> & {
  // Optional original slide index. When provided, it will be used to determine active state
  // which is more reliable with looped carousels that create DOM clones.
  slideIndex?: number;
};

export const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, slideIndex, ...props }, ref) => {
    const { selectedIndex } = useCarousel();
    const itemRef = React.useRef<HTMLDivElement | null>(null);

    // Note: We intentionally use a local ref for internal calculations and
    // avoid mutating the forwarded ref to prevent TS readonly ref issues.
    const setLocalRef = React.useCallback((node: HTMLDivElement | null) => {
      itemRef.current = node;
      if (typeof ref === 'function') ref(node);
      // If a RefObject was passed, we skip assigning to .current to avoid
      // readonly property linting errors. Consumers generally don't rely on
      // direct node ref for CarouselItem.
    }, [ref]);

    // Fallback DOM-based index if slideIndex isn't provided.
    const [domIndex, setDomIndex] = React.useState(-1);
    React.useEffect(() => {
      if (!slideIndex && itemRef.current) {
        const parent = itemRef.current.parentElement;
        if (parent) {
          const idx = Array.from(parent.children).indexOf(itemRef.current);
          setDomIndex(idx);
        }
      }
    }, [slideIndex]);

    const effectiveIndex = typeof slideIndex === 'number' ? slideIndex : domIndex;

    return (
      <div
        ref={setLocalRef}
        data-state={effectiveIndex === selectedIndex ? 'active' : 'inactive'}
        className={cn('min-w-0 shrink-0 grow-0 basis-full pl-2 sm:pl-4', className)}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = 'CarouselItem';
